import {
  ARRAY_ITEM_TOKEN,
  ROOT_SCHEMA_TYPES,
  StructuredOutputNodeType,
  StructuredOutputRootSchemaType,
  StructuredOutputSchemaNode,
  StructuredOutputSchemaPath,
  StructuredOutputSchemaTreeEntry,
  StructuredOutputStringNode,
  UnsupportedSchemaFields,
} from './types';

type TreeLabels = {
  rootName: string;
  arrayItemName: string;
};

const cloneUnsupported = (value?: UnsupportedSchemaFields) =>
  value ? (JSON.parse(JSON.stringify(value)) as UnsupportedSchemaFields) : undefined;

export const createDefaultSchemaNode = (
  type: StructuredOutputNodeType,
): StructuredOutputSchemaNode => {
  switch (type) {
    case 'object':
      return { type, properties: {}, required: [] };
    case 'array':
      return { type, items: createDefaultSchemaNode('string') };
    case 'string':
      return { type };
    case 'number':
      return { type };
    case 'integer':
      return { type };
    case 'boolean':
      return { type };
    default:
      return { type: 'object', properties: {}, required: [] };
  }
};

export const createDefaultRootSchemaNode = (
  type: StructuredOutputRootSchemaType = 'object',
): StructuredOutputSchemaNode => createDefaultSchemaNode(type);

export const cloneSchemaNode = (node: StructuredOutputSchemaNode): StructuredOutputSchemaNode => {
  switch (node.type) {
    case 'object':
      return {
        type: 'object',
        description: node.description,
        unsupported: cloneUnsupported(node.unsupported),
        required: [...node.required],
        properties: Object.fromEntries(
          Object.entries(node.properties).map(([key, value]) => [key, cloneSchemaNode(value)]),
        ),
      };
    case 'array':
      return {
        type: 'array',
        description: node.description,
        unsupported: cloneUnsupported(node.unsupported),
        items: cloneSchemaNode(node.items),
      };
    case 'string':
      return {
        type: 'string',
        description: node.description,
        unsupported: cloneUnsupported(node.unsupported),
        enum: node.enum ? [...node.enum] : undefined,
      };
    case 'number':
    case 'integer':
    case 'boolean':
      return {
        type: node.type,
        description: node.description,
        unsupported: cloneUnsupported(node.unsupported),
      };
    default:
      return createDefaultSchemaNode('object');
  }
};

export const pathToKey = (path: StructuredOutputSchemaPath) => JSON.stringify(path);

export const keyToPath = (key: string): StructuredOutputSchemaPath => {
  try {
    const parsed = JSON.parse(key);
    return Array.isArray(parsed) ? parsed.filter((segment) => typeof segment === 'string') : [];
  } catch {
    return [];
  }
};

export const formatSchemaPath = (path: StructuredOutputSchemaPath, rootName = 'root') => {
  if (!path.length) {
    return rootName;
  }

  return [rootName, ...path].join('.').replace(`.${ARRAY_ITEM_TOKEN}`, ARRAY_ITEM_TOKEN);
};

export const getParentPath = (path: StructuredOutputSchemaPath) =>
  path.length ? path.slice(0, -1) : null;

export const getPathFieldName = (path: StructuredOutputSchemaPath) => {
  const fieldName = path[path.length - 1];
  return fieldName && fieldName !== ARRAY_ITEM_TOKEN ? fieldName : null;
};

export const getNodeAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
): StructuredOutputSchemaNode | null => {
  if (!path.length) {
    return root;
  }

  const [segment, ...rest] = path;

  if (segment === ARRAY_ITEM_TOKEN) {
    return root.type === 'array' ? getNodeAtPath(root.items, rest) : null;
  }

  if (root.type !== 'object') {
    return null;
  }

  const child = root.properties[segment];
  return child ? getNodeAtPath(child, rest) : null;
};

export const updateNodeAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
  updater: (node: StructuredOutputSchemaNode) => StructuredOutputSchemaNode,
): StructuredOutputSchemaNode => {
  if (!path.length) {
    return updater(cloneSchemaNode(root));
  }

  const [segment, ...rest] = path;

  if (segment === ARRAY_ITEM_TOKEN && root.type === 'array') {
    return {
      ...root,
      items: updateNodeAtPath(root.items, rest, updater),
    };
  }

  if (root.type === 'object' && root.properties[segment]) {
    return {
      ...root,
      properties: {
        ...root.properties,
        [segment]: updateNodeAtPath(root.properties[segment], rest, updater),
      },
    };
  }

  return root;
};

export const buildSchemaTreeEntries = (
  root: StructuredOutputSchemaNode,
  labels: TreeLabels,
): StructuredOutputSchemaTreeEntry[] => {
  const entries: StructuredOutputSchemaTreeEntry[] = [];

  const visit = (
    node: StructuredOutputSchemaNode,
    path: StructuredOutputSchemaPath,
    depth: number,
  ) => {
    entries.push({
      path,
      pathKey: pathToKey(path),
      name:
        depth === 0
          ? labels.rootName
          : path[path.length - 1] === ARRAY_ITEM_TOKEN
            ? labels.arrayItemName
            : path[path.length - 1],
      depth,
      node,
    });

    if (node.type === 'object') {
      Object.entries(node.properties).forEach(([propertyName, propertyNode]) => {
        visit(propertyNode, [...path, propertyName], depth + 1);
      });
    }

    if (node.type === 'array') {
      visit(node.items, [...path, ARRAY_ITEM_TOKEN], depth + 1);
    }
  };

  visit(root, [], 0);

  return entries;
};

export const createUniquePropertyName = (
  existingKeys: string[],
  baseName = 'field',
  excludedKey?: string,
) => {
  const normalizedBase = baseName.trim() || 'field';
  const existing = new Set(existingKeys.filter((key) => key !== excludedKey));

  if (!existing.has(normalizedBase)) {
    return normalizedBase;
  }

  let index = 1;
  let nextName = `${normalizedBase}_${index}`;

  while (existing.has(nextName)) {
    index += 1;
    nextName = `${normalizedBase}_${index}`;
  }

  return nextName;
};

export const addObjectPropertyAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
  type: StructuredOutputNodeType = 'string',
) => {
  const targetNode = getNodeAtPath(root, path);

  if (!targetNode || targetNode.type !== 'object') {
    return { value: root, createdPath: path };
  }

  const nextName = createUniquePropertyName(Object.keys(targetNode.properties), 'field');
  const nextValue = updateNodeAtPath(root, path, (node) => {
    if (node.type !== 'object') {
      return node;
    }

    return {
      ...node,
      properties: {
        ...node.properties,
        [nextName]: createDefaultSchemaNode(type),
      },
    };
  });

  return {
    value: nextValue,
    createdPath: [...path, nextName],
  };
};

export const deleteObjectPropertyAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
) => {
  const parentPath = getParentPath(path);
  const fieldName = getPathFieldName(path);

  if (!parentPath || !fieldName) {
    return root;
  }

  return updateNodeAtPath(root, parentPath, (node) => {
    if (node.type !== 'object') {
      return node;
    }

    const nextProperties = { ...node.properties };
    delete nextProperties[fieldName];

    return {
      ...node,
      properties: nextProperties,
      required: node.required.filter((requiredField) => requiredField !== fieldName),
    };
  });
};

export const renameObjectPropertyAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
  nextName: string,
) => {
  const parentPath = getParentPath(path);
  const fieldName = getPathFieldName(path);

  if (!parentPath || !fieldName) {
    return { value: root, nextPath: path };
  }

  const parentNode = getNodeAtPath(root, parentPath);

  if (!parentNode || parentNode.type !== 'object') {
    return { value: root, nextPath: path };
  }

  const normalizedName = createUniquePropertyName(
    Object.keys(parentNode.properties),
    nextName,
    fieldName,
  );

  if (normalizedName === fieldName) {
    return { value: root, nextPath: [...parentPath, normalizedName] };
  }

  const nextValue = updateNodeAtPath(root, parentPath, (node) => {
    if (node.type !== 'object') {
      return node;
    }

    const renamedProperties = Object.fromEntries(
      Object.entries(node.properties).map(([propertyName, propertyNode]) => [
        propertyName === fieldName ? normalizedName : propertyName,
        propertyNode,
      ]),
    );

    return {
      ...node,
      properties: renamedProperties,
      required: node.required.map((requiredField) =>
        requiredField === fieldName ? normalizedName : requiredField,
      ),
    };
  });

  return {
    value: nextValue,
    nextPath: [...parentPath, normalizedName],
  };
};

export const setNodeDescriptionAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
  description: string,
) =>
  updateNodeAtPath(root, path, (node) => ({
    ...node,
    description: description.trim() ? description : undefined,
  }));

export const setStringNodeEnumAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
  enumValues: string[],
) =>
  updateNodeAtPath(root, path, (node) => {
    if (node.type !== 'string') {
      return node;
    }

    return {
      ...node,
      enum: enumValues.length ? enumValues : undefined,
    } satisfies StructuredOutputStringNode;
  });

export const toggleRequiredAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
  nextRequired: boolean,
) => {
  const parentPath = getParentPath(path);
  const fieldName = getPathFieldName(path);

  if (!parentPath || !fieldName) {
    return root;
  }

  return updateNodeAtPath(root, parentPath, (node) => {
    if (node.type !== 'object') {
      return node;
    }

    const requiredSet = new Set(node.required);

    if (nextRequired) {
      requiredSet.add(fieldName);
    } else {
      requiredSet.delete(fieldName);
    }

    return {
      ...node,
      required: Array.from(requiredSet),
    };
  });
};

export const isRequiredAtPath = (
  root: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath,
) => {
  const parentPath = getParentPath(path);
  const fieldName = getPathFieldName(path);

  if (!parentPath || !fieldName) {
    return false;
  }

  const parentNode = getNodeAtPath(root, parentPath);
  return parentNode?.type === 'object' ? parentNode.required.includes(fieldName) : false;
};

export const changeNodeType = (
  node: StructuredOutputSchemaNode,
  nextType: StructuredOutputNodeType,
): StructuredOutputSchemaNode => {
  if (node.type === nextType) {
    return cloneSchemaNode(node);
  }

  const base = {
    description: node.description,
    unsupported: cloneUnsupported(node.unsupported),
  };

  switch (nextType) {
    case 'object':
      return { type: 'object', properties: {}, required: [], ...base };
    case 'array':
      return { type: 'array', items: createDefaultSchemaNode('string'), ...base };
    case 'string':
      return { type: 'string', ...base };
    case 'number':
      return { type: 'number', ...base };
    case 'integer':
      return { type: 'integer', ...base };
    case 'boolean':
      return { type: 'boolean', ...base };
    default:
      return createDefaultSchemaNode('object');
  }
};

export const isDestructiveTypeChange = (
  node: StructuredOutputSchemaNode,
  nextType: StructuredOutputNodeType,
) => {
  if (node.type === nextType) {
    return false;
  }

  if (node.type === 'object') {
    return Object.keys(node.properties).length > 0;
  }

  if (node.type === 'array') {
    return true;
  }

  if (node.type === 'string') {
    return Boolean(node.enum?.length);
  }

  return false;
};

export const hasUnsupportedFields = (node: StructuredOutputSchemaNode): boolean => {
  if (node.unsupported && Object.keys(node.unsupported).length > 0) {
    return true;
  }

  if (node.type === 'object') {
    return Object.values(node.properties).some((propertyNode) =>
      hasUnsupportedFields(propertyNode),
    );
  }

  if (node.type === 'array') {
    return hasUnsupportedFields(node.items);
  }

  return false;
};

export const countUnsupportedFields = (node: StructuredOutputSchemaNode): number => {
  const currentCount = node.unsupported ? Object.keys(node.unsupported).length : 0;

  if (node.type === 'object') {
    return (
      currentCount +
      Object.values(node.properties).reduce(
        (count, propertyNode) => count + countUnsupportedFields(propertyNode),
        0,
      )
    );
  }

  if (node.type === 'array') {
    return currentCount + countUnsupportedFields(node.items);
  }

  return currentCount;
};

export const collectUnsupportedPaths = (
  node: StructuredOutputSchemaNode,
  path: StructuredOutputSchemaPath = [],
): string[] => {
  const paths: string[] = [];

  if (node.unsupported && Object.keys(node.unsupported).length > 0) {
    paths.push(formatSchemaPath(path));
  }

  if (node.type === 'object') {
    Object.entries(node.properties).forEach(([propertyName, propertyNode]) => {
      paths.push(...collectUnsupportedPaths(propertyNode, [...path, propertyName]));
    });
  }

  if (node.type === 'array') {
    paths.push(...collectUnsupportedPaths(node.items, [...path, ARRAY_ITEM_TOKEN]));
  }

  return paths;
};

export const serializeSelectedNodePreview = (node: StructuredOutputSchemaNode) =>
  JSON.stringify(node, null, 2);

export const validateStructuredOutputSchemaEditorValue = (
  value: StructuredOutputSchemaNode,
  parseError?: string | null,
) => {
  if (parseError) {
    return parseError;
  }

  if (value.type === 'object' && Object.keys(value.properties).length === 0) {
    return 'Object schema must define at least one property.';
  }

  return null;
};

export const ROOT_TYPE_OPTIONS = [...ROOT_SCHEMA_TYPES];
